import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { Sketch } from 'vue-color';
import tinycolor from 'tinycolor2';
import audioPlayerLoader from '@editor/droplets/audio-player/components/_preview/loader';
import formLoader from '@editor/droplets/form/components/_preview/loader';
import videoPlayerLoader from '@editor/droplets/video-player/components/_preview/loader';
import { onFormStyleUpdates } from '@editor/droplets/form/components/_edit-options/on-update';

export default Vue.component('color-picker', {
  template: require('./template.html'),
  name: 'color-picker',
  mixins: [onFormStyleUpdates],
  computed: {
    ...mapGetters(['viewport', 'activeDroplet', 'activeDropletItem']),
    schemeColors() {
      return [{ color: '', type: 'picker' }];
    },
    previews() {
      return {
        'audio-player-loader': audioPlayerLoader,
        'form-loader': formLoader,
        'video-player-loader': videoPlayerLoader,
      };
    },
    allColumns() {
      let list = [];
      const droplet = this.activeDroplet;
      if (droplet.rows) {
        _.forEach(this.activeDroplet.rows,
          row => _.forEach(row.columns, column => list.push(column)));
      }
      if (droplet.cards) {
        list = list.concat(droplet.cards);
      }
      if (droplet.columns) {
        list = list.concat(droplet.columns);
      }
      return { list };
    },
    hasAreas() {
      return Object.keys(this.areas).length > 0;
    },
    areaChecked() {
      return this.hasAreas && !this.areas.all && Object.values(this.areas).includes(true);
    },
  },
  components: {
    'sketch-picker': Sketch,
  },
  props: ['option', 'active-state', 'dom-nodes', 'component-config'],
  data() {
    return {
      pickerVals: {},
      colorPickers: {},
      debTime: 250,
      innerDomNodes: {},
      areas: {},
    };
  },
  watch: {
    viewport() {
      this.setInitialValue();
    },
    activeState() {
      this.setInitialValue();
    },
    activeDropletItem: {
      handler() {
        this.setInitialValue();
      },
      deep: true,
    },
  },
  created() {
    // setup defaults from payload models
    _.forOwn(this.option.payload.models, (model, name) => {
      let value = model.default;
      if (name === 'pickerVals') {
        value = this._defaultColorPickerObj;
      }
      this.$set(this[name], model.name, value);
    });
    if (this.option.payload.areas) {
      _.forOwn(this.option.payload.areas, (area) => {
        this.$set(this.areas, area.property, area.property === 'all');
      });
    }
    // then check for previous values
    this.setInitialValue();
  },
  methods: {
    colorPickerChange(params) {
      this._debounce(() => {
        const payload = {};
        const value = this.pickerVals[params.models.pickerVals.name];
        payload.drp = this.activeDroplet;
        payload.val = this._concatPicker(value);
        payload.viewport = this.viewport;
        let updates = params.updates;
        // if there is at least one area checked besides all
        if (this.hasAreas && this.areaChecked) {
          const checked = _.keys(_.pickBy(this.areas));
          updates = _.filter(updates, update => checked.includes(update.property));
        }
        _.forEach(updates, (item) => {
          let selector;
          if (this.activeDropletItem._id && this.componentConfig.itemList) {
            // this is in the event a user is targeting a specific item within a droplet
            // eg, a nav link or a column in columns
            const inner = this.componentConfig.itemList.selectors[item.property];
            const nested = inner || '';
            selector = `#drp-${this.activeDropletItem._id}${nested}${this.activeState}`;
          } else {
            this.resetCustomQueries(item);
            selector = item.selector.replace(/\$\{state\}/gi, this.activeState);
            selector = selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
          }
          payload.property = item.property;
          payload.selector = selector;
          payload.noId = item.noId;
          if (item.valueHelper) {
            payload.val = this[item.valueHelper](payload.val);
          }
          if (item.onUpdate) {
            this[item.onUpdate](payload);
          }
          if (!item.void) {
            this.$emit('check-legacy-styles', payload);
            this.$store.dispatch('changeDropletCss', payload);
          }
        });
        this.$emit('css-generator', this.cssPayload(), this.option.plugin);
        this.$emit('update-changes', true);
      }, this.debTime);
    },
    resetCustomQueries(update) {
      if (!this.componentConfig.itemList) {
        return;
      }
      const inner = this.componentConfig.itemList.selectors[update.property];
      const nested = inner || '';
      const customRule = new RegExp(`#drp-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}${nested}`, 'gi');
      _.forEach(this.activeDroplet.mediaQueries, (rule) => {
        if (rule.selector.match(customRule)) {
          const property = _.find(rule.properties, r => r.property === update.property);
          if (property) {
            this.$store.dispatch('setDropletVal', {
              drp: property,
              key: this.viewport,
              val: 'default',
            });
          }
        }
      });
    },
    setInitialValue() {
      const option = this.option;
      const $vm = {
        viewport: this.viewport,
        droplet: this.activeDroplet,
      };
      let rule = option.payload.updates[0];
      // if one area is checked, get that rule from the updates list
      if (this.areaChecked) {
        const checked = _.keys(_.pickBy(this.areas));
        rule = _.find(option.payload.updates, update => checked.includes(update.property));
      }
      let selector;
      if (this.activeDropletItem._id && this.componentConfig.itemList) {
        const inner = this.componentConfig.itemList.selectors[rule.property];
        const nested = inner || '';
        selector = `#drp-${this.activeDropletItem._id}${nested}${this.activeState}`;
      } else {
        selector = rule.selector.replace(/\$\{state\}/gi, this.activeState);
        selector = selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
      }
      const property = rule.property;
      const useRoot = option.$el === 'root';
      const $root = this.domNodes.$dropletRow;
      const $el = useRoot ? $root : $root.querySelectorAll(option.$el)[0];
      this.pickerVals[option.payload.models.pickerVals.name] = this._getPickerVal(
        $vm,
        selector,
        property,
        $el,
      );
    },
    checkArea(area) {
      _.forOwn(this.areas, (value, key) => {
        if (area !== key) {
          this.areas[key] = false;
        }
      });
      if ((area === 'all' && !this.areas.all) || (area !== 'all' && !this.areaChecked)) {
        this.areas.all = true;
      }
      this.setInitialValue();
    },
    shadeHelper(val) {
      const color = tinycolor(val);
      let shade;
      if (color.isDark()) {
        shade = color.lighten(30).toString();
      } else {
        shade = color.darken(30).toString();
      }
      return shade;
    },
    cssPayload() {
      let payload = false;
      if (this.option.payload.useColumns) {
        payload = {
          key: 'drps',
          value: [...this.allColumns.list, this.activeDroplet],
        };
      }
      return payload;
    },
    toggleColorPicker(type) {
      _.forOwn(this.colorPickers, (value, key) => {
        if (type && key !== type) {
          this.colorPickers[key] = false;
        } else if (!type) {
          this.colorPickers[key] = false;
        }
      });
      if (type) {
        this.colorPickers[type] = !this.colorPickers[type];
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/style-sidebar/options/color-picker/index.js